import dayjs from 'dayjs';
import React from 'react';
import { useTranslation } from 'react-i18next';

import AddBagSvg from '@/components/common/AddBagSvg';
import CheckmarkSvg from '@/components/common/CheckmarkSvg';
import PencilSvg from '@/components/common/PencilSvg';
import { Cookie } from '@/tscapi/utils/cookie';

import { useAuthenticated } from './hooks/useAuthenticated';
import { useCart } from './hooks/useCart';
import { useOrder } from './hooks/useOrder';
import { en, fr } from './i18n';

interface ModifyOrderHeroProps {
    isModifyOrder: boolean;
    isBagEmpty: boolean;
}

export function ModifyOrderHero({ isModifyOrder, isBagEmpty }: ModifyOrderHeroProps) {
    const isAuthenticated = useAuthenticated();
    const cookie: Cookie = new Cookie();
    const { data } = useCart(isAuthenticated);
    const order = useOrder(isAuthenticated, cookie.ModifyOrderNumber);
    const { i18n, t } = useTranslation('shoppingbag');
    i18n.addResourceBundle('en', 'shoppingbag', en, true, true);
    i18n.addResourceBundle('fr', 'shoppingbag', fr, true, true);

    if (!order.data || !cookie.ModifyOrderNumber) {
        return null;
    }
    if (order.error) {
        return <></>;
    }

    return (
        <>
            {!isBagEmpty && isModifyOrder && (
                <div className="modify__top-notice">
                    <CheckmarkSvg className="modify__svg--checkmark" />
                    <span>
                        <b>{t('Your order has been updated!')}</b>{' '}
                        {t('Please review your changes and proceed to checkout.')}
                    </span>
                </div>
            )}
            <div className="modify__container">
                <h1 className="bag__title">{t('Modify order')}</h1>
                <div className="modify__order-container">
                    <div className="modify__order-date">
                        {t('Order date')}
                        <b>{dayjs(order.data.OrderSummary.OrderDateTime).format('dddd, MMMM D, YYYY')}</b>
                    </div>
                    <div className="modify__order-status">
                        {t('Order status')}
                        <b>{order.data.OrderSummary.Status}</b>
                    </div>
                    <div className="modify__order-number">
                        {t('Order number')}
                        <b>{order.data.OrderSummary.OrderNo}</b>
                    </div>
                </div>
                <div className="modify__additional-container">
                    <div className="modify__additional-note--top">
                        <AddBagSvg style={{ width: '60px', minWidth: '60px' }} />
                        <div>
                            <b>
                                {t(
                                    "You can add items and/or a promo code while your order is in 'Waiting to process' status.",
                                )}
                            </b>{' '}
                            {t('Your additions will be combined with or applied to the existing order.')}
                        </div>
                    </div>
                    <div className="modify__additional-button">
                        <button
                            type="button"
                            onClick={() => (location.href = '/')}
                            className="modify__button--addnew"
                            aria-label={t('Add new items')}
                        >
                            Add new items
                        </button>
                    </div>
                    <div className="modify__additional-note--bottom">
                        <PencilSvg style={{ width: '60px', minWidth: '60px' }} />
                        <div>
                            <b>{t('Additional changes to your order.')}</b>{' '}
                            {t(
                                'If you want to remove items from your order or make any other changes including changes to your EASY PAY installments, payment method or shipping method please call our',
                            )}{' '}
                            <b>{t('Customer Service at 1-888-202-0888')}</b>
                        </div>
                    </div>
                </div>
                {data?.OrderSummary?.TotalQuantity > 0 && (
                    <h2 className="bag__title--existing">{t('Updated order')}</h2>
                )}
                {data?.OrderSummary?.TotalQuantity == 0 && (
                    <h2 className="bag__title--existing">{t('Existing order')}</h2>
                )}
            </div>
        </>
    );
}
